import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export const useCaseFacilityAddress = (propName: 'hospitalCustomLocation' | 'txCenterCustomLocation') => {
  const { setValue, control } = useFormContext<CaseFormType>();
  const currentLocation = useWatch({ control, name: propName });
  const { country, state, title = '' } = currentLocation ?? {};
  const { resolveSubdivisionName } = useCountriesData();
  const stateName = resolveSubdivisionName(state, country);

  const { list: addressList } = useAddressSearch({ address: title, countryCode: country, stateName }, true);

  const onItemSelect = useCallback(
    (addressText: string) => {
      const addressItem = addressList.find((a) => a.id === addressText);

      if (addressItem) {
        const { address: location, position, title: fullAddress, timeZone } = addressItem;
        const payload = {
          addressId: addressItem.id,
          addressLine: `${location.houseNumber} ${location.street}`,
          city: location.city,
          postalCode: location.postalCode,
          longitude: position.lng,
          latitude: position.lat,
          title: fullAddress,
          timezone: timeZone.name,
        };
        setValue(propName, { ...currentLocation, ...payload });
      }
    },
    [addressList, setValue, propName, currentLocation],
  );

  const onInputChange = useCallback(
    (text: string) => {
      const payload = {
        addressId: '',
        title: text,
      };
      setValue(propName, { ...currentLocation, ...payload });
    },
    [setValue, propName, currentLocation],
  );

  return {
    addressValue: title,
    addressList,
    onItemSelect,
    onInputChange,
  };
};
